import { create } from 'zustand';
import { WebSocketMessage } from '../services/websocket';
import { useNotificationStore } from './notificationStore';

export interface DriverPosition {
  driverId: string;
  driverName: string;
  employeeId: string;
  latitude: number | null;
  longitude: number | null;
  lastSeen: string | null;
  trackingActive: boolean;
  loadId: string | null;
  loadNumber: string | null;
  loadStatus: string | null;
  currentStopSequence: number | null;
  etaStops: EtaStop[];
  speed: number | null;
  alertStatus: 'OK' | 'OVERSTAY' | 'UNPLANNED_STOP' | 'BREAKDOWN';
}

export interface EtaStop {
  stopId: string;
  stopSequence: number;
  facilityName: string | null;
  city: string | null;
  eta: string | null;
}

export interface Alert {
  id: string;
  type: string;
  driverId: string;
  driverName: string;
  message: string;
  timestamp: string;
  read: boolean;
  payload: Record<string, unknown>;
}

interface DispatchState {
  driverPositions: Map<string, DriverPosition>;
  alerts: Alert[];
  selectedDriverId: string | null;
  isWsConnected: boolean;
  unreadAlertCount: number;

  setDriverPosition: (position: DriverPosition) => void;
  updateDriverLocation: (driverId: string, lat: number, lng: number, timestamp: string) => void;
  addAlert: (alert: Omit<Alert, 'id' | 'read'>) => void;
  markAlertsRead: () => void;
  selectDriver: (driverId: string | null) => void;
  setWsConnected: (connected: boolean) => void;
  loadInitialState: (data: { drivers: DriverPosition[] }) => void;
}

export const useDispatchStore = create<DispatchState>((set) => ({
  driverPositions: new Map(),
  alerts: [],
  selectedDriverId: null,
  isWsConnected: false,
  unreadAlertCount: 0,

  setDriverPosition: (position) =>
    set((state) => {
      const newMap = new Map(state.driverPositions);
      newMap.set(position.driverId, position);
      return { driverPositions: newMap };
    }),

  updateDriverLocation: (driverId, lat, lng, timestamp) =>
    set((state) => {
      const newMap = new Map(state.driverPositions);
      const existing = newMap.get(driverId);
      if (existing) {
        // Reject a point older than the one we already hold.
        //
        // Now that the mobile app buffers GPS while out of coverage and
        // uploads the backlog on reconnect, live updates no longer arrive in
        // chronological order. Overwriting unconditionally made the dispatch
        // map walk backwards in time and then sit on a stale position — while
        // still presenting it as the driver's current location.
        const incoming = Date.parse(timestamp);
        const current = existing.lastSeen ? Date.parse(existing.lastSeen) : NaN;
        if (!Number.isNaN(incoming) && !Number.isNaN(current) && incoming < current) {
          return { driverPositions: state.driverPositions };
        }
        newMap.set(driverId, {
          ...existing,
          latitude: lat,
          longitude: lng,
          lastSeen: timestamp,
        });
      }
      return { driverPositions: newMap };
    }),

  addAlert: (alertData) =>
    set((state) => {
      const alert: Alert = {
        ...alertData,
        id: `${Date.now()}-${Math.random()}`,
        read: false,
      };
      return {
        alerts: [alert, ...state.alerts].slice(0, 100), // Keep last 100 alerts
        unreadAlertCount: state.unreadAlertCount + 1,
      };
    }),

  markAlertsRead: () =>
    set((state) => ({
      alerts: state.alerts.map((a) => ({ ...a, read: true })),
      unreadAlertCount: 0,
    })),

  selectDriver: (driverId) => set({ selectedDriverId: driverId }),

  setWsConnected: (connected) => set({ isWsConnected: connected }),

  loadInitialState: (data) => {
    const newMap = new Map<string, DriverPosition>();
    data.drivers.forEach((d) => {
      // The Python backend sends all fields in snake_case.
      // Cast to a loose record so we can read either naming convention.
      const r = d as unknown as Record<string, unknown>;

      const normaliseStop = (s: Record<string, unknown>): EtaStop => ({
        stopId:       (s['stopId']        ?? s['stop_id']        ?? '') as string,
        stopSequence: (s['stopSequence']  ?? s['stop_sequence']  ?? 0)  as number,
        facilityName: (s['facilityName']  ?? s['facility_name']  ?? null) as string | null,
        city:         (s['city']          ?? null) as string | null,
        eta:          (s['eta']           ?? null) as string | null,
      });

      const rawStops = (r['etaStops'] ?? r['eta_stops'] ?? []) as Record<string, unknown>[];
      const driverId = (r['driverId'] ?? r['driver_id'] ?? '') as string;

      const position: DriverPosition = {
        driverId,
        driverName:           (r['driverName']          ?? r['driver_name']           ?? '') as string,
        employeeId:           (r['employeeId']           ?? r['employee_id']           ?? '') as string,
        latitude:             (r['latitude']             ?? r['current_latitude']      ?? null) as number | null,
        longitude:            (r['longitude']            ?? r['current_longitude']     ?? null) as number | null,
        lastSeen:             (r['lastSeen']             ?? r['last_seen']             ?? null) as string | null,
        trackingActive:       (r['trackingActive']       ?? r['tracking_active']       ?? false) as boolean,
        loadId:               (r['loadId']               ?? r['load_id']              ?? null) as string | null,
        loadNumber:           (r['loadNumber']           ?? r['load_number']           ?? null) as string | null,
        loadStatus:           (r['loadStatus']           ?? r['load_status']           ?? null) as string | null,
        currentStopSequence:  (r['currentStopSequence']  ?? r['current_stop_sequence'] ?? null) as number | null,
        etaStops:             rawStops.map(normaliseStop),
        speed:                (r['speed']                ?? r['speedKmh'] ?? r['speed_kmh'] ?? null) as number | null,
        alertStatus:          (r['alertStatus']          ?? r['alert_status']          ?? 'OK') as DriverPosition['alertStatus'],
      };

      if (driverId) newMap.set(driverId, position);
    });
    set({ driverPositions: newMap });
  },
}));

// WebSocket message handlers — wire up in Dashboard component
export function handleWebSocketMessage(
  message: WebSocketMessage,
  store: ReturnType<typeof useDispatchStore.getState>
) {
  switch (message.type) {
    case 'INITIAL_STATE': {
      const payload = message.payload as { drivers: DriverPosition[] };
      if (payload?.drivers) {
        store.loadInitialState(payload);
      }
      break;
    }

    case 'LOCATION_UPDATE': {
      const payload = message.payload as {
        latitude: number;
        longitude: number;
        recorded_at?: string;
      };
      if (message.driver_id && payload) {
        store.updateDriverLocation(
          message.driver_id,
          payload.latitude,
          payload.longitude,
          // Prefer the device's own timestamp over the envelope's. For a
          // backfilled offline trail these differ by hours, and only the
          // former says when the driver was actually at that point.
          payload.recorded_at ?? message.timestamp
        );
      }
      break;
    }

    case 'GEOFENCE_ALERT': {
      store.addAlert({
        type: 'GEOFENCE',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'Driver',
        message: `${message.driver_name} ${message.payload?.event_type === 'ENTRY' ? 'arrived at' : 'left'} ${message.payload?.stop_name || 'a stop'}`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      break;
    }

    case 'OVERSTAY_ALERT': {
      const p = message.payload as { duration_minutes: number; is_planned_stop: boolean };
      store.addAlert({
        type: 'OVERSTAY',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'Driver',
        message: `${message.driver_name} has been stationary for ${p?.duration_minutes?.toFixed(0)} minutes`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      // Update driver alert status
      const pos = store.driverPositions.get(message.driver_id || '');
      if (pos) {
        store.setDriverPosition({ ...pos, alertStatus: 'OVERSTAY' });
      }
      break;
    }

    case 'UNPLANNED_STOP_ALERT': {
      const p = message.payload as { duration_minutes: number };
      store.addAlert({
        type: 'UNPLANNED_STOP',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'Driver',
        message: `${message.driver_name} is at an unplanned location for ${p?.duration_minutes?.toFixed(0)} minutes`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      const pos = store.driverPositions.get(message.driver_id || '');
      if (pos) {
        store.setDriverPosition({ ...pos, alertStatus: 'UNPLANNED_STOP' });
      }
      break;
    }

    case 'STOP_AUTO_CONFIRMED': {
      const p = message.payload as { stop_name?: string; entry_count?: number };
      store.addAlert({
        type: 'AUTO_CONFIRM',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'Driver',
        message: `Auto-confirmed arrival at ${p?.stop_name || 'a stop'} (${p?.entry_count || 0} GPS readings)`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      break;
    }

    case 'STOP_DEPARTURE_CONFIRMED': {
      const p = message.payload as { dwell_seconds?: number };
      const dwellMin = p?.dwell_seconds ? Math.round(p.dwell_seconds / 60) : 0;
      store.addAlert({
        type: 'AUTO_CONFIRM',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'Driver',
        message: `Auto-confirmed departure (dwell: ${dwellMin} min)`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      break;
    }

    case 'LOAD_AUTO_STARTED': {
      const payload = message.payload as {
        load_id?: string;
        load_number?: string;
        origin_stop_id?: string;
        actual_start?: string;
        trigger?: string;
      };
      const driverPos = store.driverPositions.get(message.driver_id || '');
      const driverName = driverPos?.driverName || message.driver_name || 'Driver';
      const loadNumber = payload.load_number || 'Unknown';
      store.addAlert({
        type: 'info',
        driverId: message.driver_id || '',
        driverName: driverName,
        message: `${driverName}: Load ${loadNumber} auto-started (pickup confirmed via geofence)`,
        timestamp: message.timestamp || new Date().toISOString(),
        payload: message.payload || {},
      });
      break;
    }

    case 'BREAK_EVENT': {
      const subtype = message.subtype as string;
      store.addAlert({
        type: 'BREAK',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'Driver',
        message: `${message.driver_name} ${subtype === 'STARTED' ? 'started a break' : subtype === 'AUTO_ENDED' ? 'break auto-ended' : 'ended break'}`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      break;
    }

    case 'DRIVER_MESSAGE': {
      const p = message.payload as { message_id?: string; message_text?: string; load_number?: string };
      store.addAlert({
        type: 'DRIVER_MESSAGE',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'Driver',
        message: `Message from ${message.driver_name}: ${(p?.message_text || '').substring(0, 100)}`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      // Also push to notification store for the bell badge
      useNotificationStore.getState().addNotification({
        id: p?.message_id || Date.now().toString(),
        notification_type: 'DRIVER_MESSAGE',
        title: `Message from ${message.driver_name || 'Driver'}`,
        body: (p?.message_text || '').substring(0, 200),
        data: message.payload as Record<string, unknown>,
        load_id: (p?.load_number as string) || null,
        message_id: p?.message_id || null,
        sender_id: message.driver_id || null,
        sender_name: message.driver_name || null,
        is_read: false,
        created_at: message.timestamp || new Date().toISOString(),
      });
      break;
    }

    case 'LOAD_STOPS_CHANGED': {
      const p = message.payload as { load_number?: string; changes?: string[] };
      store.addAlert({
        type: 'LOAD_STOPS_CHANGED',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'System',
        message: `Route updated for ${p?.load_number || 'a load'}: ${(p?.changes || []).join('; ').substring(0, 150)}`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      useNotificationStore.getState().addNotification({
        id: Date.now().toString(),
        notification_type: 'LOAD_STOPS_CHANGED',
        title: `Load ${p?.load_number || ''} — Route Updated`,
        body: (p?.changes || []).join('; ').substring(0, 200),
        data: message.payload as Record<string, unknown>,
        load_id: null,
        message_id: null,
        sender_id: null,
        sender_name: null,
        is_read: false,
        created_at: message.timestamp || new Date().toISOString(),
      });
      break;
    }

    case 'LOAD_SCHEDULE_REVISED': {
      // Cascade fired upstream on this driver — one or more downstream loads
      // have had their schedule revised. Show a single alert summarising the
      // chain; the Dashboard data refetch will pull the new revised_start/end
      // and the triage queue will surface a CASCADE_RISK row per shifted load.
      const p = message.payload as {
        triggering_load_number?: string;
        triggered_slip_minutes?: number;
        shifts?: Array<{ load_number: string; shift_minutes: number }>;
      };
      const shiftCount = p?.shifts?.length ?? 0;
      const summary = shiftCount === 1 && p?.shifts?.[0]
        ? `${p.shifts[0].load_number} shifts by ${p.shifts[0].shift_minutes}m`
        : `${shiftCount} downstream loads shifted`;
      store.addAlert({
        type: 'LOAD_SCHEDULE_REVISED',
        driverId: message.driver_id || '',
        driverName: message.driver_name || 'System',
        message: `Cascade from ${p?.triggering_load_number || 'a load'} (+${p?.triggered_slip_minutes ?? 0}m): ${summary}`,
        timestamp: message.timestamp,
        payload: message.payload || {},
      });
      useNotificationStore.getState().addNotification({
        id: Date.now().toString(),
        notification_type: 'LOAD_SCHEDULE_REVISED',
        title: `Schedule revised — cascade from ${p?.triggering_load_number || ''}`,
        body: summary,
        data: message.payload as Record<string, unknown>,
        load_id: null,
        message_id: null,
        sender_id: null,
        sender_name: null,
        is_read: false,
        created_at: message.timestamp || new Date().toISOString(),
      });
      break;
    }
  }
}
